import { openDB, type DBSchema, type IDBPDatabase } from 'idb';

/**
 * Minimal IndexedDB wrapper used by the project slice to mirror the
 * authoritative server data locally so the app can render instantly on
 * cold start / poor connectivity.
 *
 * Contract that callers depend on (do NOT change without a migration plan):
 *   - DB name:      'wherecam'
 *   - DB version:   1
 *   - Object stores: 'projects', 'floorPlans'
 *   - Both stores use keyPath 'id' with autoIncrement, so callers can `set`
 *     a value without supplying an explicit key.
 *   - Public helpers: `set(store, value)` and `clear(store)`.
 */

const VALID_STORES = ['projects', 'floorPlans'] as const;

/** Union of the two valid object-store names. */
export type StoreName = (typeof VALID_STORES)[number];

/**
 * idb schema describing both object stores. Records are stored as opaque
 * objects keyed by an auto-incremented `id`; we don't constrain the value
 * shape here because the slice persists whole domain entities verbatim.
 */
interface WhereCamDB extends DBSchema {
  projects: {
    key: number;
    value: unknown;
  };
  floorPlans: {
    key: number;
    value: unknown;
  };
}

/**
 * Narrow an arbitrary string to a valid store name. Defensive: callers in
 * this codebase always pass literals, but this guards against accidental
 * typos reaching a transaction with an unknown store.
 */
function assertValidStore(storeName: string): asserts storeName is StoreName {
  if (!VALID_STORES.includes(storeName as StoreName)) {
    throw new Error(
      `Invalid IndexedDB store "${storeName}". Valid stores: ${VALID_STORES.join(', ')}`,
    );
  }
}

let dbPromise: Promise<IDBPDatabase<WhereCamDB>> | null = null;

/**
 * Lazily open (and memoize) the singleton database connection. The first
 * caller triggers the `upgrade` callback which creates any missing stores.
 */
function getDb(): Promise<IDBPDatabase<WhereCamDB>> {
  if (!dbPromise) {
    dbPromise = openDB<WhereCamDB>('wherecam', 1, {
      upgrade(db) {
        for (const name of VALID_STORES) {
          if (!db.objectStoreNames.contains(name)) {
            // Use autoIncrement so callers don't need to provide an id.
            db.createObjectStore(name, { keyPath: 'id', autoIncrement: true });
          }
        }
      },
    });
  }
  return dbPromise;
}

/** Remove every record from the given store. */
export async function clear(storeName: StoreName): Promise<void> {
  assertValidStore(storeName);
  const db = await getDb();
  const tx = db.transaction(storeName, 'readwrite');
  await tx.store.clear();
  await tx.done;
}

/** Insert or replace a single record in the given store. */
export async function set(storeName: StoreName, value: unknown): Promise<void> {
  assertValidStore(storeName);
  const db = await getDb();
  const tx = db.transaction(storeName, 'readwrite');
  await tx.store.put(value);
  await tx.done;
}
